import React, { useState, useEffect } from 'react';
import { User, Mail, PenSquare } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { supabase } from '../lib/supabase';
import { Link } from 'react-router-dom';
import { Logo } from '../components/Logo';
import FriendsListDialog from '../components/FriendsListDialog';
import PostActions from '../components/PostActions';
import PostAttachment from '../components/PostAttachment';

interface Post {
  id: string;
  content: string;
  created_at: string;
  song: {
    id: string;
    title: string;
    artist: string;
    album: string;
    album_cover_url: string;
  } | null;
  attachments: Array<{
    id: string;
    type: 'link' | 'image' | 'file';
    url: string;
    title?: string;
    description?: string;
    thumbnail_url?: string;
  }>;
  likes: number;
  comments: number;
  liked_by_me: boolean;
}

function Profile() {
  const { user } = useUser();
  const [showFriendsList, setShowFriendsList] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [friendsCount, setFriendsCount] = useState(0);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.email) {
        setUserEmail(session.user.email);
      }
    };
    getSession();
  }, []);

  useEffect(() => {
    if (user) {
      fetchFriendsCount();
      fetchPosts();
    }
  }, [user]);

  const fetchFriendsCount = async () => {
    try {
      const { count } = await supabase
        .from('friend_requests')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'accepted')
        .or(`sender_id.eq.${user?.id},receiver_id.eq.${user?.id}`);

      setFriendsCount(count || 0);
    } catch (error) {
      console.error('Error fetching friends count:', error);
    }
  };

  const fetchPosts = async () => {
    if (!user) return;
    
    try {
      const { data: postsData, error: postsError } = await supabase
        .from('posts')
        .select(`
          id,
          content,
          created_at,
          song:songs(id, title, artist, album, album_cover_url),
          attachments:post_attachments(
            id,
            type,
            url,
            title,
            description,
            thumbnail_url
          ),
          likes:likes(count),
          comments:comments(count)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (postsError) throw postsError;

      const { data: userLikes } = await supabase
        .from('likes')
        .select('post_id')
        .eq('user_id', user.id);

      const likedPostIds = new Set(userLikes?.map(like => like.post_id) || []);

      const formattedPosts = postsData?.map(post => ({
        ...post,
        attachments: post.attachments || [],
        likes: post.likes[0]?.count ?? 0,
        comments: post.comments[0]?.count ?? 0,
        liked_by_me: likedPostIds.has(post.id)
      })) || [];

      setPosts(formattedPosts);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="mobile-header">
        <div className="mobile-header-content">
          <div className="flex items-center gap-2">
            <Logo className="h-8 w-8 text-indigo-600" />
            <span className="text-lg font-bold text-indigo-600">Profile</span>
          </div>
        </div>
      </header>

      <div className="mobile-main pt-2">
        <div className="max-w-2xl mx-auto space-y-4">
          {/* Profile Header */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="relative h-48 bg-gradient-to-r from-indigo-500 to-purple-500">
              {user.coverImage && (
                <img
                  src={user.coverImage}
                  alt="Cover"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              )}
              <div className="absolute -bottom-16 left-8">
                <div className="w-32 h-32 rounded-full border-4 border-white bg-gray-200 flex items-center justify-center overflow-hidden">
                  {user.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="h-12 w-12 text-gray-400" />
                  )}
                </div>
              </div>
            </div>
            <div className="pt-20 pb-6 px-8">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{user.displayName || user.username}</h1>
                <p className="text-gray-600">@{user.username}</p>
                <div className="flex items-center mt-2 text-gray-600">
                  <Mail className="h-4 w-4 mr-2" />
                  <span>{userEmail}</span>
                </div>
                <p className="text-gray-500 mt-2">{user.bio}</p>
              </div>

              <div className="flex items-center space-x-8 mt-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{posts.length}</div>
                  <div className="text-sm text-gray-600">Posts</div>
                </div>
                <button
                  onClick={() => setShowFriendsList(true)}
                  className="text-center hover:opacity-75 transition-opacity"
                >
                  <div className="text-2xl font-bold text-gray-900">{friendsCount}</div>
                  <div className="text-sm text-gray-600">Friends</div>
                </button>
              </div>
            </div>
          </div>

          {/* Posts */}
          <div className="space-y-4">
            {loading ? (
              <div className="bg-white rounded-lg shadow-md p-8">
                <div className="flex flex-col items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  <p className="text-gray-600 mt-4">Loading posts...</p>
                </div>
              </div>
            ) : posts.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-8 text-center">
                <PenSquare className="h-12 w-12 text-indigo-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No posts yet</h3>
                <p className="text-gray-600 mb-6">Share your musical journey with your friends</p>
                <Link
                  to="/"
                  className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <PenSquare className="h-5 w-5 mr-2" />
                  Create your first post
                </Link>
              </div>
            ) : (
              posts.map(post => (
                <div key={post.id} className="bg-white rounded-lg shadow-md p-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden">
                      {user.avatarUrl ? (
                        <img 
                          src={user.avatarUrl} 
                          alt={user.username}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="h-6 w-6 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold">
                        {user.displayName || user.username}
                      </div>
                      <p className="text-sm text-gray-500">
                        {new Date(post.created_at).toLocaleString()}
                      </p>
                      
                      <p className="text-gray-900 mt-2">{post.content}</p>
                      
                      {post.song && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg flex items-center space-x-3">
                          <img
                            src={post.song.album_cover_url}
                            alt={post.song.album}
                            className="w-12 h-12 rounded-md"
                          />
                          <div>
                            <p className="font-medium">{post.song.title}</p>
                            <p className="text-sm text-gray-600">{post.song.artist}</p>
                          </div>
                        </div>
                      )}

                      {post.attachments?.map((attachment) => (
                        <PostAttachment
                          key={attachment.id}
                          type={attachment.type}
                          url={attachment.url}
                          title={attachment.title}
                          description={attachment.description}
                          thumbnailUrl={attachment.thumbnail_url}
                        />
                      ))}

                      <PostActions
                        postId={post.id}
                        userId={user.id}
                        likes={post.likes}
                        comments={post.comments}
                        liked={post.liked_by_me}
                        onLikeChange={fetchPosts}
                        onDelete={fetchPosts}
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <FriendsListDialog
            isOpen={showFriendsList}
            onClose={() => setShowFriendsList(false)}
            userId={user.id}
          />
        </div>
      </div>
    </div>
  );
}

export default Profile;